import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_POLICY = {
  principal_id: "",
  principal_type: "user",
  display_name: "",
  speaker_ids: [],
  zone_ids: [],
  permissions: ["view"],
};

const PERMISSIONS = [
  { id: "view", label: "Consulter" },
  { id: "volume", label: "Modifier le volume" },
  { id: "mute", label: "Mute / Unmute" },
  { id: "playback", label: "Contrôler la diffusion" },
];

function getSpeakerId(speaker) {
  return String(
    speaker.id ||
      speaker.deviceId ||
      speaker.uuid ||
      speaker.mac ||
      speaker.mac_address ||
      speaker.ip_address ||
      ""
  );
}

function getZoneId(zone) {
  return String(zone.id || zone.name || "");
}

export default function AdminAccessPanel({ onClose }) {
  const [policies, setPolicies] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [zones, setZones] = useState([]);
  const [policy, setPolicy] = useState(EMPTY_POLICY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [policyResponse, speakersResponse, zonesResponse] = await Promise.all([
        axios.get(`${API}/admin/access-policies`),
        axios.get(`${API}/speakers`),
        axios.get(`${API}/axis-zones`),
      ]);
      setPolicies(policyResponse.data.policies || []);
      setSpeakers(Array.isArray(speakersResponse.data) ? speakersResponse.data : []);
      setZones(zonesResponse.data.zones || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Impossible de charger les droits d'accès");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sortedSpeakers = useMemo(
    () => [...speakers].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [speakers]
  );

  const sortedZones = useMemo(
    () => [...zones].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [zones]
  );

  const toggleListValue = (field, value) => {
    setPolicy((current) => {
      const values = current[field] || [];
      return {
        ...current,
        [field]: values.includes(value)
          ? values.filter((item) => item !== value)
          : [...values, value],
      };
    });
  };

  const editPolicy = (existing) => {
    setPolicy({
      principal_id: existing.principal_id || "",
      principal_type: existing.principal_type || "user",
      display_name: existing.display_name || "",
      speaker_ids: existing.speaker_ids || [],
      zone_ids: existing.zone_ids || [],
      permissions: existing.permissions || ["view"],
    });
    setMessage("");
    setError("");
  };

  const newPolicy = () => {
    setPolicy({ ...EMPTY_POLICY, speaker_ids: [], zone_ids: [], permissions: ["view"] });
    setMessage("");
    setError("");
  };

  const savePolicy = async (event) => {
    event.preventDefault();
    const principalId = policy.principal_id.trim();
    if (!principalId) {
      setError("L'OID Entra de l'utilisateur ou du groupe est obligatoire.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");
    try {
      await axios.put(`${API}/admin/access-policies/${encodeURIComponent(principalId)}`, {
        ...policy,
        principal_id: principalId,
      });
      setMessage("Droits enregistrés.");
      await loadData();
      setPolicy((current) => ({ ...current, principal_id: principalId }));
    } catch (err) {
      setError(err.response?.data?.detail || "Échec de l'enregistrement des droits");
    } finally {
      setSaving(false);
    }
  };

  const deletePolicy = async (principalId) => {
    if (!window.confirm("Supprimer cette règle d'accès ?")) return;
    setError("");
    setMessage("");
    try {
      await axios.delete(`${API}/admin/access-policies/${encodeURIComponent(principalId)}`);
      if (policy.principal_id === principalId) newPolicy();
      setMessage("Règle supprimée.");
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || "Échec de la suppression");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black bg-opacity-70 overflow-y-auto">
      <div className="min-h-screen px-4 py-8">
        <div className="mx-auto max-w-7xl rounded-xl border border-gray-700 bg-gray-900 text-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-700 px-6 py-4">
            <div>
              <h2 className="text-2xl font-bold">Administration des droits</h2>
              <p className="mt-1 text-sm text-gray-400">
                Les rôles Axis.Admin / Axis.Operator / Axis.Viewer sont attribués dans Microsoft Entra. Ici, vous définissez le périmètre des Operator et Viewer.
              </p>
            </div>
            <button onClick={onClose} className="rounded bg-gray-700 px-4 py-2 hover:bg-gray-600">Fermer</button>
          </div>

          {error && <div className="mx-6 mt-4 rounded bg-red-900/60 border border-red-700 p-3 text-red-100">{error}</div>}
          {message && <div className="mx-6 mt-4 rounded bg-green-900/50 border border-green-700 p-3 text-green-100">{message}</div>}

          {loading ? (
            <div className="p-8 text-center text-gray-300">Chargement des droits...</div>
          ) : (
            <div className="grid gap-6 p-6 lg:grid-cols-[360px_1fr]">
              <aside className="rounded-lg border border-gray-700 bg-gray-800 p-4">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold">Règles existantes</h3>
                  <button onClick={newPolicy} className="rounded bg-blue-600 px-3 py-1.5 text-sm hover:bg-blue-500">Nouvelle</button>
                </div>
                {policies.length === 0 ? (
                  <p className="text-sm text-gray-400">Aucune règle. Sans règle explicite, un Operator/Viewer ne voit aucune ressource.</p>
                ) : (
                  <div className="space-y-2">
                    {policies.map((item) => (
                      <div key={item.principal_id} className="rounded border border-gray-700 bg-gray-900 p-3">
                        <button className="w-full text-left" onClick={() => editPolicy(item)}>
                          <div className="font-medium">{item.display_name || item.principal_id}</div>
                          <div className="mt-1 break-all text-xs text-gray-400">{item.principal_type} · {item.principal_id}</div>
                          <div className="mt-1 text-xs text-gray-500">
                            {item.speaker_ids?.length || 0} enceinte(s) · {item.zone_ids?.length || 0} zone(s)
                          </div>
                        </button>
                        <button onClick={() => deletePolicy(item.principal_id)} className="mt-2 text-xs text-red-400 hover:text-red-300">Supprimer</button>
                      </div>
                    ))}
                  </div>
                )}
              </aside>

              <form onSubmit={savePolicy} className="space-y-6">
                <section className="rounded-lg border border-gray-700 bg-gray-800 p-5">
                  <h3 className="mb-4 text-lg font-semibold">Principal Entra</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="block">
                      <span className="text-sm text-gray-300">Nom d'affichage</span>
                      <input value={policy.display_name} onChange={(e) => setPolicy({ ...policy, display_name: e.target.value })} className="mt-1 w-full rounded border border-gray-600 bg-gray-900 px-3 py-2" placeholder="Ex. Service Culture" />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-300">Type</span>
                      <select value={policy.principal_type} onChange={(e) => setPolicy({ ...policy, principal_type: e.target.value })} className="mt-1 w-full rounded border border-gray-600 bg-gray-900 px-3 py-2">
                        <option value="user">Utilisateur</option>
                        <option value="group">Groupe Entra</option>
                      </select>
                    </label>
                  </div>
                  <label className="mt-4 block">
                    <span className="text-sm text-gray-300">Object ID (OID) Entra</span>
                    <input value={policy.principal_id} onChange={(e) => setPolicy({ ...policy, principal_id: e.target.value.trim() })} className="mt-1 w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 font-mono text-sm" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" required />
                  </label>
                </section>

                <section className="rounded-lg border border-gray-700 bg-gray-800 p-5">
                  <h3 className="mb-3 text-lg font-semibold">Permissions</h3>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {PERMISSIONS.map((permission) => (
                      <label key={permission.id} className="flex items-center gap-2 rounded bg-gray-900 px-3 py-2">
                        <input type="checkbox" checked={policy.permissions.includes(permission.id)} onChange={() => toggleListValue("permissions", permission.id)} />
                        <span>{permission.label}</span>
                      </label>
                    ))}
                  </div>
                </section>

                <section className="rounded-lg border border-gray-700 bg-gray-800 p-5">
                  <h3 className="mb-3 text-lg font-semibold">Enceintes autorisées</h3>
                  {sortedSpeakers.length === 0 ? <p className="text-sm text-gray-400">Aucune enceinte disponible.</p> : (
                    <div className="grid max-h-64 gap-2 overflow-y-auto md:grid-cols-2">
                      {sortedSpeakers.map((speaker) => {
                        const id = getSpeakerId(speaker);
                        return (
                          <label key={id} className="flex items-start gap-2 rounded bg-gray-900 p-3">
                            <input className="mt-1" type="checkbox" checked={policy.speaker_ids.includes(id)} onChange={() => toggleListValue("speaker_ids", id)} />
                            <span><span className="block font-medium">{speaker.name || id}</span><span className="block text-xs text-gray-400">{speaker.model || "Axis"} · {speaker.ip_address || speaker.mac_address || id}</span></span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </section>

                <section className="rounded-lg border border-gray-700 bg-gray-800 p-5">
                  <h3 className="mb-3 text-lg font-semibold">Zones Axis autorisées</h3>
                  {sortedZones.length === 0 ? <p className="text-sm text-gray-400">Aucune zone Axis disponible.</p> : (
                    <div className="grid max-h-64 gap-2 overflow-y-auto md:grid-cols-2">
                      {sortedZones.map((zone) => {
                        const id = getZoneId(zone);
                        return (
                          <label key={id} className="flex items-start gap-2 rounded bg-gray-900 p-3">
                            <input className="mt-1" type="checkbox" checked={policy.zone_ids.includes(id)} onChange={() => toggleListValue("zone_ids", id)} />
                            <span><span className="block font-medium">{zone.name || id}</span><span className="block text-xs text-gray-400">{zone.type || "Zone Axis"} · {id}</span></span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </section>

                <div className="flex justify-end">
                  <button disabled={saving} className="rounded bg-blue-600 px-5 py-2.5 font-semibold hover:bg-blue-500 disabled:opacity-50">
                    {saving ? "Enregistrement..." : "Enregistrer les droits"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
